import { upperFirst } from 'lodash';

import { Limit, LimitFragment, OfferFragment } from '@occc/graphql';
import { limitHelper } from '@occc-app/offerHelpers/utils/limitHelper';

import { getTransactionFrequencyLimitInitialValues } from './transaction-frequency-limit';
import { getAwardCountLimitInitialValues } from './award-count-limit';
import {
  LimitFormValue,
  LimitScope,
  LimitType,
  LimitTimePeriod,
  LimitInTime,
  LimitsGroupFormValue,
  LimitsInConstraintGroupFormValue,
} from './types';

const isLimitInTime = (limit: LimitFragment): limit is LimitInTime => {
  return limit.__typename.includes('InTime');
};

const isLimitOfType = (limit: Limit, limitType: LimitType) =>
  limit.__typename.includes(upperFirst(limitType));

export const hasLimitOfType = (limits: Limit[], limitType: LimitType) =>
  !!limits.find((limit) => isLimitOfType(limit, limitType));

export const transformLimitToLimitFormValue = (limit: Limit): LimitFormValue => ({
  isEnabled: true,
  id: limit.id,
  limit: limit.limit,
  timePeriod: isLimitInTime(limit) ? limit.timePeriod : LimitTimePeriod.Total,
});

const getLimitScope = (limit: LimitFragment): LimitScope | null => {
  const isTransaction = limit.__typename === 'LimitInTransactionAwardCount';
  if (isTransaction) {
    return LimitScope.Transaction;
  }

  const isMembershipScope = limit.__typename.includes('Membership');
  if (isMembershipScope) {
    return LimitScope.Membership;
  }

  const isGlobalScope = limit.__typename.includes('Global');
  if (isGlobalScope) {
    return LimitScope.Global;
  }

  return null;
};

const isStackedAwardCountFulfillmentLimit = (limit: LimitFragment) =>
  limitHelper.isMembershipAwardCount(limit) ||
  limitHelper.isMembershipAwardCountInTime(limit) ||
  limitHelper.isGlobalAwardCount(limit) ||
  limitHelper.isGlobalAwardCountInTime(limit);

export const transformOfferToLimitFormValues = (
  offer: OfferFragment,
  limitType: LimitType,
  defaultFormValues?: Partial<LimitsGroupFormValue>,
  options?: { stacked?: boolean },
): LimitsGroupFormValue => {
  const formValues: LimitsGroupFormValue = {
    global: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    membership: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    ...(defaultFormValues || {}),
  };

  if (!hasLimitOfType(offer.limits, limitType)) {
    return formValues;
  }

  offer.limits
    .filter(
      (limit) =>
        isLimitOfType(limit, limitType) &&
        !limitHelper.isMembershipProgressInTime(limit) &&
        !limitHelper.isInTransactionProgress(limit) &&
        !(options?.stacked && isStackedAwardCountFulfillmentLimit(limit)),
    )
    .forEach((limit) => {
      const scope = getLimitScope(limit);

      if (scope) {
        formValues[scope] = transformLimitToLimitFormValue(limit);
      }
    });

  return formValues;
};

const hasEnabledChildLimit = (
  values: Partial<LimitsGroupFormValue | LimitsInConstraintGroupFormValue>,
) => {
  const childLimits = [
    values.membership,
    values.global,
    values.transaction,
    values.progressPerPeriod,
    values.progressPerTransaction,
  ];

  return childLimits.some(
    (limit) => Boolean(limit?.isEnabled) && limit?.limit !== undefined && limit?.limit !== null,
  );
};

export const getAwardCountInitialValues = (
  offer: OfferFragment,
  defaultValues?: Partial<LimitsGroupFormValue | LimitsInConstraintGroupFormValue>,
  options?: { excludeTotal?: boolean; stacked?: boolean },
): LimitsInConstraintGroupFormValue => {
  const stacked = options?.stacked ?? false;
  const transactionFrequency = getTransactionFrequencyLimitInitialValues(offer, options);
  const membershipAwardCountLimits = stacked
    ? getAwardCountLimitInitialValues(offer, 'membership')
    : undefined;
  const globalAwardCountLimits = stacked
    ? getAwardCountLimitInitialValues(offer, 'global')
    : undefined;
  const values = {
    ...(defaultValues || {}),
    ...transformOfferToLimitFormValues(offer, LimitType.AwardCount, defaultValues, { stacked }),
    transactionFrequency,
    membershipAwardCountLimits,
    globalAwardCountLimits,
  };

  return {
    ...values,
    isEnabled:
      hasLimitOfType(offer.limits, LimitType.AwardCount) ||
      hasEnabledChildLimit(values) ||
      (transactionFrequency?.isEnabled ?? false) ||
      (membershipAwardCountLimits?.isEnabled ?? false) ||
      (globalAwardCountLimits?.isEnabled ?? false),
  } as LimitsInConstraintGroupFormValue;
};

export const getIsTransactionLimitOnlyEnabled = (values: LimitsInConstraintGroupFormValue) => {
  return (
    values.transaction?.isEnabled &&
    !values.global.isEnabled &&
    !values.membership.isEnabled &&
    !values.progressPerPeriod?.isEnabled &&
    !values.progressPerTransaction?.isEnabled
  );
};
